import { AsyncPipe } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  Validators,
} from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { firstValueFrom } from "rxjs";
import { filter, map, withLatestFrom } from "rxjs/operators";
import { UserService } from "src/app/core/auth";
import { ButtonComponent } from "src/app/core/elements/button.component";
import { FormComponent } from "src/app/core/form.component";
import { CheckboxComponent } from "src/app/core/forms/checkbox.component";
import { TextareaComponent } from "src/app/core/forms/textarea.component";
import { TextboxComponent } from "src/app/core/forms/textbox.component";
import { SitesService } from "src/app/core/sites";
import {
  HelpAssistEventType,
  HelpAssistTicketType,
} from "src/app/partner/help-assist/help-assist-enums";
import { HelpAssistService } from "src/app/partner/help-assist/help-assist.service";
import { HelpAssistTicketReference } from "src/app/partner/help-assist/models/ticket/base-help-assist-ticket.model";
import { HelpAssistTicketCreate } from "src/app/partner/help-assist/models/ticket/help-assist-ticket-create.model";
import { environment } from "src/environments";
import { emailValidator, isExistent } from "src/utils";

interface FormValues {
  emailAddress: string;
  question: string;
  orders: string | null;
  isOrderEntrySkipped: boolean;
}

@UntilDestroy()
@Component({
  selector: "mr-general-ticket-form",
  templateUrl: "./general-ticket-form.component.html",
  styleUrls: ["./general-ticket-form.component.scss"],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TextboxComponent,
    TextareaComponent,
    CheckboxComponent,
    ButtonComponent,
    AsyncPipe,
  ],
})
export class HelpAssistGeneralTicketFormComponent
  extends FormComponent<FormValues>
  implements OnInit
{
  public constructor(
    formBuilder: UntypedFormBuilder,
    private readonly sites: SitesService,
    private readonly helpAssist: HelpAssistService,
    user: UserService,
  ) {
    super(formBuilder, {
      emailAddress: [
        user.details.emailAddress,
        [Validators.required, emailValidator],
      ],
      isOrderEntrySkipped: [false],
      orders: [null],
      question: [null, Validators.required],
    });
  }

  @Input() public isAppointmentCreation = false;
  @Input() public orderSearchTerms?: readonly string[];
  @Output() public readonly submitSuccess =
    new EventEmitter<HelpAssistTicketReference>();
  @Output() public readonly submitError = new EventEmitter<unknown>();

  public readonly helpAssistAskUsPhoneNumber =
    environment.helpAssist.askUsPhoneNumber;

  public readonly areGeneralTicketsDisabledChanges =
    this.sites.selectedChanges.pipe(
      map((site) => !site.helpAssist.areGeneralTicketsAllowed),
    );

  public ngOnInit(): void {
    this.areGeneralTicketsDisabledChanges
      .pipe(untilDestroyed(this))
      .subscribe((areGeneralTicketsDisabled) => {
        if (areGeneralTicketsDisabled) {
          this.form.disable();
        } else {
          this.form.enable();
        }
      });

    if (this.isAppointmentCreation) {
      if (this.orderSearchTerms) {
        this.setValue("orders", this.orderSearchTerms.join(", "));
      }

      this.getValueChanges("isOrderEntrySkipped")
        .pipe(
          filter(isExistent),
          withLatestFrom(this.areGeneralTicketsDisabledChanges),
          untilDestroyed(this),
        )
        .subscribe(([isOrderEntrySkipped, areGeneralTicketsDisabled]) => {
          this.reconfigure("orders", {
            isDisabled: isOrderEntrySkipped || areGeneralTicketsDisabled,
            validators:
              isOrderEntrySkipped || areGeneralTicketsDisabled
                ? null
                : [Validators.required, Validators.maxLength(8000)],
          });
        });
    }
  }

  public async submit(): Promise<void> {
    const site = await firstValueFrom(this.sites.selectedChanges);

    try {
      const successResponse = await this.runSubmit(
        ({ emailAddress, isOrderEntrySkipped, orders, question }) => {
          const request = new HelpAssistTicketCreate({
            schedule: null,
            emailAddress,
            events: [
              {
                comment: question,
                type: HelpAssistEventType.Commented,
              },
            ],
            orders: isOrderEntrySkipped ? null : orders,
            phoneNumber: null,
            site,
            type: HelpAssistTicketType.General,
          });

          return this.helpAssist.create(request);
        },
      );

      if (successResponse) {
        this.submitSuccess.emit(successResponse.value);
      }
    } catch (error) {
      this.submitError.emit(error);
    }
  }
}
